/**
 * Pure helpers for the cross-client "Recommended for you" feature.
 *
 * The PWA and the desktop app both compute recommendations with the
 * exact same weighted-all-seeds algorithm, upload the result to
 * `users/{uid}/library/recommendations`, and read the cloud aggregate
 * on startup. Because the algorithm is deterministic, if two clients
 * start from the same set of user ratings they produce identical
 * outputs — so the client whose local input fingerprint matches the
 * cloud fingerprint can skip the TMDB fan-out entirely and just
 * render the cached cloud result.
 *
 * This module only contains the pure hash function so both clients
 * compute byte-identical fingerprints. The full algorithm lives in
 * `packages/pwa/src/discover/useRecommendations.ts` and
 * `src/main/services/WeightedRecommendationService.ts`.
 */

export interface RatingFingerprintInput {
  /** Rated movies — each entry MUST have a user rating. */
  movies: Array<{ tmdbId: number; userRating: number }>
  /** Rated series — each entry MUST have a user rating. */
  series: Array<{ tmdbId: number; userRating: number }>
}

/**
 * Fingerprint format version. Bumping this invalidates any cloud
 * aggregate written under an older algorithm so clients recompute
 * from scratch — old and new fingerprints will never compare equal.
 *
 *   v1 — linear weighting, candidate.vote_average >= 6 filter
 *   v2 — quadratic weighting (seed.userRating²), seed.userRating >= 6
 *        filter, no candidate rating filter
 *   v3 — same as v2, plus candidate.voteCount >= 1000 filter
 *   v4 — same as v3, but MAX_RESULTS bumped 30 → 60
 *   v5 — per-seed fetch widened from 1 page (20 items) to 3 pages
 *        (~60 items per seed) and MAX_RESULTS raised to 200 so load-
 *        more can keep going for users with dense libraries
 */
const FINGERPRINT_VERSION = 'v5'

/**
 * Deterministic fingerprint of every rated title in the user's
 * library. The format is:
 *
 *   v5|m:<tmdbId>:<rating>,...|s:<tmdbId>:<rating>,...
 *
 * Both lists are sorted by tmdbId ascending before hashing so the
 * output is stable regardless of insertion order. Any rating change,
 * addition, or removal bumps the hash; unrelated library writes
 * (updatedAt bumps, progress ticks, watchlist changes) leave it
 * untouched. The version prefix ensures an algorithm change (like
 * the v1 → v2 migration) forces every client to recompute and
 * overwrite the old cloud aggregate.
 *
 * The return value is an opaque string — treat it as a black-box
 * equality key, not a semantic description of the input.
 */
export function fingerprintRatings(input: RatingFingerprintInput): string {
  const m = [...input.movies]
    .sort((a, b) => a.tmdbId - b.tmdbId)
    .map((e) => `${e.tmdbId}:${e.userRating}`)
    .join(',')
  const s = [...input.series]
    .sort((a, b) => a.tmdbId - b.tmdbId)
    .map((e) => `${e.tmdbId}:${e.userRating}`)
    .join(',')
  return `${FINGERPRINT_VERSION}|m:${m}|s:${s}`
}
